import { useQuery } from '@tanstack/react-query';
import API_ENDPOINTS from 'services/urls';
import axiosInstance from 'ns_libs/nsAxios';

/**
 * ProFormaSummary interface for displaying automated pro formas in tables
 * Only includes fields actually used by DealAutomatedTable component
 * Compatible with IProForma from useGetProFormasByDeal
 */
export interface ProFormaSummary {
    id: number;
    name: string;
    proFormaStatusId: number;
    proFormaModelId: number | null;
    proFormaModelName: string | null;
    boeScenarioId: number | null;
    boeScenario: { id: number; name: string } | null;
    isActive: boolean;
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy: string;
    draftStep: number;
    subprojectId?: string | null; // Optional for compatibility with IProForma
}

/**
 * UploadedProFormaSummary interface for displaying manual/uploaded pro formas in tables
 * Only includes fields actually used by ManualSpreadsheetTable component
 * Compatible with IUploadedProForma (all other fields are optional for compatibility)
 */
export interface UploadedProFormaSummary {
    id: number;
    createdAt: string;
    createdBy: string | null;
    fileSize: number | null;
    s3FilePath: string;
    // Optional fields for compatibility with IUploadedProForma
    dealTypeId?: number | null;
    organizationId?: string;
    dealId?: number;
    updatedAt?: string;
    updatedBy?: string | null;
    s3Bucket?: string;
    s3Url?: string;
    unleveredIrr?: number | null;
    leveredIrr?: number | null;
    limitedPartnerIrr?: number | null;
    generalPartnerIrr?: number | null;
    untrendedYieldOnCost?: number | null;
    trendedYieldOnCost?: number | null;
    effectiveGrossRevenue?: number | null;
    totalOperatingExpenses?: number | null;
    netOperatingIncome?: number | null;
    capitalExpenditures?: number | null;
    cashFlowFromOps?: number | null;
    valuation?: number | null;
    acquisitionCosts?: number | null;
    hardCosts?: number | null;
    softCosts?: number | null;
    financingCosts?: number | null;
    totalUses?: number | null;
    sources?: any[];
    excelMapping?: string;
    sheetNames?: string[];
}

export interface DealFromSignup {
    id: number;
    dealName: string;
    proFormas: ProFormaSummary[];
    uploadedProFormas: UploadedProFormaSummary[];
    assetTypeCorrelationId?: string | null;
    description?: string | null;
}

interface GetDealsWithProFormasParams {
    orgId: string;
    excludeProFormaId?: number;
    dealName?: string;
    statusId?: number;
    includeAllDealsWithProFormas?: boolean;
}

const getDealsWithProFormas = async ({
    orgId,
    excludeProFormaId,
    dealName,
    statusId,
    includeAllDealsWithProFormas,
}: GetDealsWithProFormasParams): Promise<DealFromSignup[]> => {
    const params: Record<string, any> = {};
    if (excludeProFormaId) params.excludeProFormaId = excludeProFormaId;
    if (dealName) params.dealName = dealName;
    if (statusId !== undefined) params.statusId = statusId;
    if (includeAllDealsWithProFormas !== undefined) params.includeAllDealsWithProFormas = includeAllDealsWithProFormas;

    const response = await axiosInstance.get(API_ENDPOINTS.ORGANIZATION_DEALS_WITH_PRO_FORMAS(orgId), { params });
    return response.data;
};

const useGetDealsWithProFormas = ({
    orgId,
    excludeProFormaId,
    dealName,
    statusId,
    includeAllDealsWithProFormas,
    enabled = true,
}: GetDealsWithProFormasParams & { enabled?: boolean }) => useQuery({
    queryKey: ['deals-with-pro-formas', orgId, excludeProFormaId, dealName, statusId, includeAllDealsWithProFormas],
    queryFn: () => getDealsWithProFormas({
        orgId, excludeProFormaId, dealName, statusId, includeAllDealsWithProFormas,
    }),
    enabled,
});

export default useGetDealsWithProFormas;
